/**
 * Sysadmin Fine-Tuning rail — TanStack Query hooks (Phase 5a).
 *
 * Backs the 4 v1 sub-tabs:
 *   - /sysadmin/fine-tuning/adapters      → useFtAdapters + useCreateFtAdapter + adapter mutations
 *   - /sysadmin/fine-tuning/training-data → useFtTrainingData + approve/reject mutations
 *   - /sysadmin/fine-tuning/golden-set    → useFtGoldenSet + CRUD mutations
 *   - /sysadmin/fine-tuning/evaluation    → useFtEvaluationRuns + transition / approve / reject
 *
 * Schemas live HERE so the wire types stay in lockstep with the
 * Fastify route Zod schemas (manual sync; codegen target v1.1).
 *
 * Refresh cadence:
 *   - Adapters list:     10s (default)
 *   - Training data:     30s (slower-changing curation queue)
 *   - Golden set:        30s
 *   - Evaluation runs:    5s while any run is in a non-terminal state
 *                         (route layer doesn't differentiate; the UI
 *                         re-mounts the hook with explicit refreshMs
 *                         when a non-terminal run is in view)
 */

import { z } from 'zod';
import {
  createSysadminQuery,
  createSysadminMutation,
} from './use-sysadmin-query';
import { ExportControlSchema, SensitivitySchema } from '@argusai/shared';

// ════════════════════════════════════════════════════════════════════════
//  Shared enum schemas
// ════════════════════════════════════════════════════════════════════════

export const FtTrainingStatusSchema = z.enum([
  'pending',
  'queued',
  'training',
  'complete',
  'failed',
]);
export type FtTrainingStatus = z.infer<typeof FtTrainingStatusSchema>;

export const FtBlueGreenStateSchema = z.enum([
  'inactive',
  'candidate',
  'active',
  'retired',
  'rolled_back',
]);
export type FtBlueGreenState = z.infer<typeof FtBlueGreenStateSchema>;

export const FtTrainingDataSourceSchema = z.enum([
  'user_feedback',
  'golden_dataset',
  'expert_curated',
  'auto_collected',
]);
export type FtTrainingDataSource = z.infer<typeof FtTrainingDataSourceSchema>;

export const FtClassificationSchema = SensitivitySchema;
export type FtClassification = z.infer<typeof FtClassificationSchema>;

export const FtExportControlSchema = ExportControlSchema;
export type FtExportControl = z.infer<typeof FtExportControlSchema>;

export const FtDifficultySchema = z.enum(['easy', 'medium', 'hard']);
export type FtDifficulty = z.infer<typeof FtDifficultySchema>;

export const FtEvalStatusSchema = z.enum([
  'queued',
  'automated_running',
  'automated_complete',
  'automated_failed',
  'safety_running',
  'safety_complete',
  'safety_failed',
  'sme_pending',
  'sme_complete',
  'sme_rejected',
  'admin_pending',
  'approved',
  'rejected',
  'failed',
]);
export type FtEvalStatus = z.infer<typeof FtEvalStatusSchema>;

// ════════════════════════════════════════════════════════════════════════
//  Adapters
// ════════════════════════════════════════════════════════════════════════

export const FtAdapterRowSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  baseModelId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  trainingStatus: FtTrainingStatusSchema,
  blueGreenState: FtBlueGreenStateSchema,
  adapterPath: z.string().nullable(),
  goldenDatasetScore: z.number().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  activatedAt: z.string().nullable(),
  retiredAt: z.string().nullable(),
});
export type FtAdapterRow = z.infer<typeof FtAdapterRowSchema>;

export const FtAdapterListResponseSchema = z.object({
  items: z.array(FtAdapterRowSchema),
});
export type FtAdapterListResponse = z.infer<typeof FtAdapterListResponseSchema>;

export const useFtAdapters = createSysadminQuery({
  key: ['sysadmin-fine-tuning-adapters'],
  url: '/admin/platform/fine-tuning/adapters',
  schema: FtAdapterListResponseSchema,
});

export const CreateFtAdapterInputSchema = z.object({
  tenantId: z.string().uuid(),
  baseModelId: z.string().min(1).max(200),
  name: z.string().min(1).max(200),
  description: z.string().max(2000).nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type CreateFtAdapterInput = z.infer<typeof CreateFtAdapterInputSchema>;

export const useCreateFtAdapter = createSysadminMutation({
  url: '/admin/platform/fine-tuning/adapters',
  method: 'POST',
  invalidates: [['sysadmin-fine-tuning-adapters']],
  outputSchema: FtAdapterRowSchema,
});

export const useActivateFtAdapter = createSysadminMutation<
  { id: string },
  FtAdapterRow
>({
  url: ({ id }) => `/admin/platform/fine-tuning/adapters/${id}/activate`,
  method: 'POST',
  invalidates: [['sysadmin-fine-tuning-adapters']],
  outputSchema: FtAdapterRowSchema,
});

export const useRollbackFtAdapter = createSysadminMutation<
  { id: string; reason?: string },
  FtAdapterRow
>({
  url: ({ id }) => `/admin/platform/fine-tuning/adapters/${id}/rollback`,
  method: 'POST',
  invalidates: [['sysadmin-fine-tuning-adapters']],
  outputSchema: FtAdapterRowSchema,
});

// ════════════════════════════════════════════════════════════════════════
//  Training Data
// ════════════════════════════════════════════════════════════════════════

export const FtTrainingDataRowSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  source: FtTrainingDataSourceSchema,
  messages: z.array(
    z.object({
      role: z.enum(['system', 'user', 'assistant']),
      content: z.string(),
    })
  ),
  qualityMetadata: z.record(z.string(), z.unknown()),
  faithfulnessScore: z.number().nullable(),
  hasPiiRedaction: z.boolean(),
  feedbackRating: z.enum(['positive', 'negative']).nullable(),
  approved: z.boolean(),
  approvedBy: z.string().uuid().nullable(),
  approvedAt: z.string().nullable(),
  sourceAuditLogId: z.string().uuid().nullable(),
  sourceMessageId: z.string().uuid().nullable(),
  createdAt: z.string(),
});
export type FtTrainingDataRow = z.infer<typeof FtTrainingDataRowSchema>;

export const FtTrainingDataListResponseSchema = z.object({
  items: z.array(FtTrainingDataRowSchema),
});

export const useFtTrainingData = createSysadminQuery({
  key: ['sysadmin-fine-tuning-training-data'],
  url: '/admin/platform/fine-tuning/training-data',
  schema: FtTrainingDataListResponseSchema,
});

export const useApproveFtTrainingData = createSysadminMutation<
  { id: string },
  FtTrainingDataRow
>({
  url: ({ id }) =>
    `/admin/platform/fine-tuning/training-data/${id}/approve`,
  method: 'POST',
  invalidates: [['sysadmin-fine-tuning-training-data']],
  outputSchema: FtTrainingDataRowSchema,
});

export const useRejectFtTrainingData = createSysadminMutation<
  { id: string; reason?: string },
  { success: boolean }
>({
  url: ({ id }) =>
    `/admin/platform/fine-tuning/training-data/${id}/reject`,
  method: 'POST',
  invalidates: [['sysadmin-fine-tuning-training-data']],
  outputSchema: z.object({ success: z.boolean() }),
});

// ════════════════════════════════════════════════════════════════════════
//  Golden Set
// ════════════════════════════════════════════════════════════════════════

export const FtGoldenPairRowSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  question: z.string(),
  expectedAnswer: z.string(),
  classification: FtClassificationSchema,
  exportControl: FtExportControlSchema,
  lastEvaluatedScore: z.number().nullable(),
  lastEvaluatedAdapterId: z.string().uuid().nullable(),
  lastEvaluatedAt: z.string().nullable(),
  category: z.string().nullable(),
  difficulty: FtDifficultySchema.nullable(),
  tags: z.array(z.string()),
  isActive: z.boolean(),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FtGoldenPairRow = z.infer<typeof FtGoldenPairRowSchema>;

export const FtGoldenSetListResponseSchema = z.object({
  items: z.array(FtGoldenPairRowSchema),
});

export const useFtGoldenSet = createSysadminQuery({
  key: ['sysadmin-fine-tuning-golden-set'],
  url: '/admin/platform/fine-tuning/golden-set',
  schema: FtGoldenSetListResponseSchema,
});

export const CreateFtGoldenPairInputSchema = z.object({
  tenantId: z.string().uuid(),
  question: z.string().min(1).max(4000),
  expectedAnswer: z.string().min(1).max(16000),
  classification: FtClassificationSchema.default('unclassified'),
  exportControl: FtExportControlSchema.default('none'),
  category: z.string().max(60).nullable().optional(),
  difficulty: FtDifficultySchema.nullable().optional(),
  tags: z.array(z.string().max(60)).max(20).default([]),
});
export type CreateFtGoldenPairInput = z.infer<
  typeof CreateFtGoldenPairInputSchema
>;

export const useCreateFtGoldenPair = createSysadminMutation<
  CreateFtGoldenPairInput,
  FtGoldenPairRow
>({
  url: '/admin/platform/fine-tuning/golden-set',
  method: 'POST',
  invalidates: [['sysadmin-fine-tuning-golden-set']],
  outputSchema: FtGoldenPairRowSchema,
});

export const useDeleteFtGoldenPair = createSysadminMutation<
  { id: string },
  { success: boolean }
>({
  url: ({ id }) => `/admin/platform/fine-tuning/golden-set/${id}`,
  method: 'DELETE',
  invalidates: [['sysadmin-fine-tuning-golden-set']],
  outputSchema: z.object({ success: z.boolean() }),
});

// ════════════════════════════════════════════════════════════════════════
//  Evaluation Runs
// ════════════════════════════════════════════════════════════════════════

export const FtEvalRunRowSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  adapterId: z.string().uuid(),
  status: FtEvalStatusSchema,
  automatedScore: z.number().nullable(),
  safetyScore: z.number().nullable(),
  smeScore: z.number().nullable(),
  aggregateScore: z.number().nullable(),
  gateResults: z.record(z.string(), z.unknown()),
  pairCount: z.number().int(),
  triggeredBy: z.string().uuid().nullable(),
  smeReviewerId: z.string().uuid().nullable(),
  adminApproverId: z.string().uuid().nullable(),
  rejectionReason: z.string().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
});
export type FtEvalRunRow = z.infer<typeof FtEvalRunRowSchema>;

export const FtEvalRunListResponseSchema = z.object({
  items: z.array(FtEvalRunRowSchema),
});

export const useFtEvaluationRuns = createSysadminQuery({
  key: ['sysadmin-fine-tuning-evaluation-runs'],
  url: '/admin/platform/fine-tuning/evaluation',
  schema: FtEvalRunListResponseSchema,
  // 5s — non-terminal runs need fast feedback; terminal runs already
  // stop emitting events so the cost is bounded.
  refreshMs: 5_000,
});

export const useCreateFtEvalRun = createSysadminMutation<
  { adapterId: string },
  FtEvalRunRow
>({
  url: '/admin/platform/fine-tuning/evaluation',
  method: 'POST',
  invalidates: [['sysadmin-fine-tuning-evaluation-runs']],
  outputSchema: FtEvalRunRowSchema,
});

export const useApproveFtEvalRun = createSysadminMutation<
  { id: string; aggregateScore: number; notes?: string },
  FtEvalRunRow
>({
  url: ({ id }) => `/admin/platform/fine-tuning/evaluation/${id}/approve`,
  method: 'POST',
  invalidates: [
    ['sysadmin-fine-tuning-evaluation-runs'],
    ['sysadmin-fine-tuning-adapters'],
  ],
  outputSchema: FtEvalRunRowSchema,
});

export const useRejectFtEvalRun = createSysadminMutation<
  { id: string; reason: string; aggregateScore?: number },
  FtEvalRunRow
>({
  url: ({ id }) => `/admin/platform/fine-tuning/evaluation/${id}/reject`,
  method: 'POST',
  invalidates: [
    ['sysadmin-fine-tuning-evaluation-runs'],
    ['sysadmin-fine-tuning-adapters'],
  ],
  outputSchema: FtEvalRunRowSchema,
});
